import React, { useState, useEffect } from "react";
import { Tooltip, Button, Popconfirm } from "antd";
import { EyeOutlined, DeleteOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { deleteSale, fetchSalesRecord } from "../SalesRecordThunk";
import { selectAuthInfo } from "../../Auth/authSelectors";
import { setDetails, setList } from "../SalesRecordSlice";
import { useIsAdminUser } from "../../../lib/accessList";
import Swal from "sweetalert2";
import { useTxLock } from "../../../hooks/useTxLock";
import { accessChecker } from "../../../lib/functions";
import VoucherPdfActions from "../../../components/common/VoucherPdfActions";

const SalesRecordActions = ({ record, setTatmeenModal }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const saleId = record?.sale_id || record?.id;
  const authInfo = useSelector(selectAuthInfo);
  const isLocked = Number(record?.is_locked) === 1;
  const isAdminUser = useIsAdminUser();
  const canEdit = authInfo?.can_edit === 1;
  const canDelete = authInfo?.can_delete === 1;
  // ✅ ADD: transaction date lock
  const { isLocked: isTxLocked, lockDateFormatted } = useTxLock();

  const txLocked = isTxLocked(record?.created_date);
  const isTatmeenUAE =
    authInfo?.is_tatmeen_enabled === "yes" &&
    authInfo?.country === "United Arab Emirates" && (authInfo?.subscription == 'PharmaERP_Pro' || authInfo?.subscription == 'PharmaERP_Standard' || authInfo?.subscription == 'PharmaERP_Basic');

  const hasTatmeenEntryAccess = accessChecker("tatmeen_entry");

  // ✅ CHANGE: include txLocked in both action guards
  const canEditAction = !isLocked && !txLocked && (isAdminUser || canEdit);
  const canDeleteAction = !isLocked && !txLocked && (isAdminUser || canDelete);

  const filters = useSelector((state) => state.salesRecord.filters);
  const handleDelete = async () => {
    const res = await dispatch(deleteSale(saleId));
    console.log(res, "res1234");

    if (res.meta.requestStatus === "fulfilled") {
      dispatch(setList([]));
      dispatch(setDetails([]));
      dispatch(
        fetchSalesRecord({
          selectedFilterType: filters.filterType,
          selectedRecordType: filters.recordType,
          filters,
        }),
      );
    } else {
      Swal.fire(res.payload || "Something went wrong", "", "warning");

    }
  };

  return (
    <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>

      {hasTatmeenEntryAccess && isTatmeenUAE && (
        <Tooltip title="Tatmeen Details">
          <Button
            type="link"
            icon={<InfoCircleOutlined />}
            onClick={() =>
              setTatmeenModal({
                open: true,
                record,
              })
            }
          />
        </Tooltip>
      )}

      <Tooltip title="View Voucher">
        <Button
          type="link"
          icon={<EyeOutlined />}
          onClick={(e) => {
            e.stopPropagation(); // ✅ prevents row click
            if (
              authInfo?.branch_company_type === "Pharmacy" &&
              record?.insurance_id
            ) {
              navigate(`/sales/quick-sales-pharma-voucher/${saleId}`);
            } else {
              navigate(`/sales/quick-sales-voucher/${saleId}`);
            }
          }}
        />
      </Tooltip>


      <>
        <Tooltip
          title={
            isLocked
              ? "Cannot edit. This sale is used in Return or Replacement"
              : txLocked
                ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
                : "Edit Voucher"
          }
        >
          <Button
            type="link"
            icon={<EditOutlined />}
            disabled={!canEditAction}
            danger
            onClick={(e) => {
              e.stopPropagation();
              if (!canEditAction) return;
              if (isLocked) return;
              if (authInfo?.branch_company_type === "Pharmacy") {
                navigate(`/pharma-sales/${saleId}`);
              } else {
                navigate(`/invoice/${saleId}`);
              }
            }}
          />
        </Tooltip>
      </>

      <Tooltip
        title={
          isLocked
            ? "Cannot delete. This sale is used in Return or Replacement"
            : txLocked
              ? `This voucher is locked. Transactions up to ${lockDateFormatted} cannot be modified.`
              : "Delete Voucher"
        }
      >
        <Popconfirm
          title="Are you sure you want to delete this sale?"
          okText="Yes, Delete"
          disabled={!canDeleteAction}
          cancelText="Cancel"
          onConfirm={handleDelete}
          onCancel={(e) => e?.stopPropagation()}
        >
          <Button
            type="link"
            danger
            disabled={!canDeleteAction}
            icon={<DeleteOutlined />}
            onClick={(e) => e.stopPropagation()}
          />
        </Popconfirm>
      </Tooltip>

      <VoucherPdfActions
        url={`/print/sales/${saleId}`}
        fileName={`Invoice_${record.sale_voucher_no}.pdf`}
      />

    </div>
  );
};

export default SalesRecordActions;
